import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Tabs } from 'antd';
import PropTypes from 'prop-types';

import BoardLinkView from '../boards/BoardLinkView';

import MaterialInfo from './MaterialInfo';
import MaterialUpload from './MaterialUpload/MaterialUpload';
import MaterialUpdateHistory from './MaterialUpdateHistory';

const { TabPane } = Tabs;

export default function MaterialDrawerTabContainer({
  item,
  isNotDisplay,
  divisionIds,
  divisionFilter,
  onDivisionChange,
  locations = [],
  onLocationsChanged,
  assemblyMaterials = [],
  onMaterialsChanged,
  totalQuantity,
  totalQuantityAllocated,
  onTabChange,
  activeTab,
  uploadFile,
  onFileChange,
  currentStep,
  onMassUploadDataChanged,
  visible,
  isAssembly,
  shouldAutoCalcCost,
  shouldAutoCalcLabourCost,
}) {
  const cardLinks = useSelector((state) => state.boards.cardLinks);
  const ourLinks = useMemo(() => {
    if (!item || !item.id || isNotDisplay) return [];
    return cardLinks.filter((cardLink) => (
      cardLink.materialId === item.id
    ));
  }, [item, isNotDisplay, cardLinks]);

  const infoView = (
    <MaterialInfo
      isNotDisplay={isNotDisplay}
      item={item}
      divisionIds={divisionIds}
      divisionFilter={divisionFilter}
      onDivisionChange={onDivisionChange}
      locations={locations}
      onLocationsChanged={onLocationsChanged}
      assemblyMaterials={assemblyMaterials}
      onMaterialsChanged={onMaterialsChanged}
      totalQuantity={totalQuantity}
      totalQuantityAllocated={totalQuantityAllocated}
      isAssembly={isAssembly}
      shouldAutoCalcCost={shouldAutoCalcCost}
      shouldAutoCalcLabourCost={shouldAutoCalcLabourCost}
    />
  );

  if (isAssembly) return infoView;

  if (!isNotDisplay || (item && item.id)) { // If we have an id we're editing.
    const {
      id: itemId,
      name: itemName,
    } = item ?? {};
    return (
      <Tabs tabBarStyle={{ paddingLeft: 24, marginBottom: 0 }}>
        <TabPane tab="Details" key="details">
          {infoView}
        </TabPane>
        <TabPane tab="Timeline" key="timeline">
          <MaterialUpdateHistory materialId={itemId} name={itemName} />
        </TabPane>
        {ourLinks.length > 0
        && (
        <TabPane tab="Cards" key="cards">
          <BoardLinkView links={ourLinks} linkType="materialId" linkId={itemId} style={{ padding: '0px 24px', marginTop: 24 }} />
        </TabPane>
        )}
      </Tabs>
    );
  }

  return (
    <Tabs
      tabPosition="left"
      tabBarStyle={{ paddingTop: 24 }}
      onChange={onTabChange}
      activeKey={activeTab}
    >
      <TabPane tab="Create" key="create" className="material-tab-pane">
        {infoView}
      </TabPane>
      <TabPane tab="Upload" key="upload" className="material-tab-pane">
        <MaterialUpload
          uploadFile={uploadFile}
          onFileChange={onFileChange}
          currentStep={currentStep}
          onMassUploadDataChanged={onMassUploadDataChanged}
          divisionIds={divisionIds}
          divisionFilter={divisionFilter}
          onDivisionChange={onDivisionChange}
          visible={visible}
        />
      </TabPane>
    </Tabs>
  );
}

MaterialDrawerTabContainer.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
  }),
  isNotDisplay: PropTypes.bool,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  divisionFilter: PropTypes.instanceOf(Set),
  onDivisionChange: PropTypes.func,
  locations: PropTypes.arrayOf(PropTypes.shape({})),
  onLocationsChanged: PropTypes.func,
  totalQuantity: PropTypes.number,
  totalQuantityAllocated: PropTypes.number,
  onTabChange: PropTypes.func,
  activeTab: PropTypes.string,
  uploadFile: PropTypes.shape({}),
  onFileChange: PropTypes.func,
  currentStep: PropTypes.number,
  onMassUploadDataChanged: PropTypes.func,
  visible: PropTypes.bool,
  isAssembly: PropTypes.bool,
  assemblyMaterials: PropTypes.arrayOf(PropTypes.shape({})),
  onMaterialsChanged: PropTypes.func,
  shouldAutoCalcCost: PropTypes.bool.isRequired,
  shouldAutoCalcLabourCost: PropTypes.bool.isRequired,
};

MaterialDrawerTabContainer.defaultProps = {
  item: null,
  isNotDisplay: false,
  divisionIds: [],
  divisionFilter: undefined,
  onDivisionChange: () => {},
  locations: [],
  onLocationsChanged: () => {},
  totalQuantity: 0,
  totalQuantityAllocated: 0,
  onTabChange: () => {},
  activeTab: 'create',
  uploadFile: null,
  onFileChange: () => {},
  currentStep: 0,
  onMassUploadDataChanged: () => {},
  visible: false,
  isAssembly: false,
  assemblyMaterials: [],
  onMaterialsChanged: () => {},
};
